import React, { useMemo } from 'react';
import { TPN_BEST_PRACTICE_5_3, nextWorkflowType } from '../../../../services/workflow';
import { useWorkflowMap } from '../../../../hooks/useWorkflowMap';
import SiteDataRow from './SiteDataRow';
import { Contact, Company } from '../../../../interfaces/users.interface';
import { Workflow, WorkflowTypeKey } from '../../../../interfaces/workflow.interface';
import { useCanAddNewWorkflow } from '../../../../hooks/useCanAddNewWorkflow';

type Props = {
  company: Company;
  site: Contact;
  fullWf: Workflow[] | undefined;
  userCanAdminCompany: boolean;
  companyCallback: (c: Company) => void;
  onRowClick: (site: Contact) => void;
  handleSetCanStartNewQuestionniare: () => void;
  statusFilter?: string;
};

export default function SiteRowWithWorkflow({
  company,
  site,
  fullWf,
  userCanAdminCompany,
  companyCallback,
  onRowClick,
  handleSetCanStartNewQuestionniare,
  statusFilter,
}: Props) {
  const workflows = useMemo(() => {
    const f = fullWf?.filter((wf) => wf.site === site.id && wf.isActive);
    return f?.sort((a, b) => b.id - a.id);
  }, [fullWf, site.id]);

  const allSiteWorkflows = useMemo(() => {
    const f = fullWf?.filter((wf) => wf.site === site.id);
    return f?.sort((a, b) => b.id - a.id);
  }, [fullWf, site.id]);

  const canAddNewWorkflow = useCanAddNewWorkflow(workflows);

  const workflowMap = useWorkflowMap();
  const nextWorkflowLabel = useMemo<WorkflowTypeKey>(() => {
    if (!workflows?.length) return TPN_BEST_PRACTICE_5_3;
    return nextWorkflowType(workflows[0].workflowType) as WorkflowTypeKey;
  }, [workflows, workflowMap]);

  const canBeginNextQuestionnaire = useMemo(
    () => !!allSiteWorkflows?.length && canAddNewWorkflow(nextWorkflowLabel),
    [allSiteWorkflows?.length, nextWorkflowLabel, canAddNewWorkflow],
  );

  return (
    <SiteDataRow
      key={site.id}
      company={company}
      site={site}
      userCanAdminCompany={userCanAdminCompany}
      companySuccessCallback={companyCallback}
      onClick={onRowClick}
      showCanBeginNewWorkflow={canBeginNextQuestionnaire}
      setCanStartNewQuestionniare={handleSetCanStartNewQuestionniare}
      statusFilter={statusFilter}
    />
  );
}
